import { businessProposalsService } from '@/services/proposals/business';
import { s3Service } from '@/services/s3';
import type { UploadedFile } from '@/shared/components/form-fields/types';
import { PortalStatus } from '@/shared/enums/portal-status.enum';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ProposalDecisionPayload } from '../types';

/** Raiz `proposals` exigida pelo presigned-upload (`/portal/v1/storage/presigned-upload`). */
const ATTACHMENTS_PREFIX = 'proposals/analysis';

/**
 * Aceite com requiresInspection = S: o backend persiste status 9 e responde 400
 * com a mensagem de encaminhamento (mesmo padrão da transmissão para análise).
 */
export class ProposalSentToInspectionError extends Error {
  readonly proposalSentToInspection = true as const;

  constructor(message?: string) {
    super(message ?? 'A proposta foi encaminhada para o fluxo de inspeção.');
    this.name = 'ProposalSentToInspectionError';
  }
}

export function isProposalSentToInspectionError(
  error: unknown
): error is ProposalSentToInspectionError {
  if (error instanceof ProposalSentToInspectionError) return true;
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as ProposalSentToInspectionError).proposalSentToInspection === true
  );
}

function indicatesSentToInspection(message?: string): boolean {
  if (!message) return false;
  const normalized = message.toLowerCase();
  return (
    normalized.includes('fluxo de inspeção') ||
    normalized.includes('fluxo de inspecao')
  );
}

async function uploadAttachments(
  proposalId: string,
  attachments: UploadedFile[]
): Promise<string[]> {
  if (!attachments?.length) return [];
  const prefix = `${ATTACHMENTS_PREFIX}/${proposalId}`;
  return Promise.all(
    attachments.map(uploaded => s3Service.uploadFile(uploaded.file, prefix))
  );
}

function invalidateAnalysisQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  proposalId: string
) {
  void queryClient.invalidateQueries({ queryKey: ['proposal-analyses'] });
  void queryClient.invalidateQueries({
    queryKey: ['proposal-analysis-records', proposalId],
  });
  void queryClient.invalidateQueries({ queryKey: ['proposal', proposalId] });
  void queryClient.invalidateQueries({ queryKey: ['proposals'] });
}

export function useProposalAnalysisDecision(proposalId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // Não retentar: 400 de encaminhamento para inspeção é fluxo esperado e o
    // retry global (mutations.retry: 1) duplicaria POST /analysis/decision.
    retry: false,
    mutationFn: async (payload: ProposalDecisionPayload) => {
      const attachmentKeys = await uploadAttachments(
        proposalId,
        payload.attachments
      );

      const response = await businessProposalsService.decideAnalysis(
        proposalId,
        {
          status: payload.decision,
          description: payload.description,
          rejectionReason: payload.rejectionReason,
          ...(attachmentKeys.length > 0 && { attachments: attachmentKeys }),
        }
      );

      if (!response.success) {
        if (indicatesSentToInspection(response.error)) {
          throw new ProposalSentToInspectionError(response.error);
        }
        throw new Error(
          response.error || 'Erro ao registrar decisão da análise'
        );
      }

      // Aceite roteado para inspeção também pode voltar como 200 com
      // `sentToInspection: true` (backend atual) ou apenas com a `message`
      // de encaminhamento (versões intermediárias). Normaliza para o mesmo
      // erro tratado pelo formulário (abre o modal de inspeção, sem toast).
      const data = response.data as
        | { sentToInspection?: boolean; message?: string }
        | undefined;
      const responseMessage = (response as { message?: string }).message;
      if (
        payload.decision === 'accepted' &&
        (data?.sentToInspection === true ||
          indicatesSentToInspection(data?.message) ||
          indicatesSentToInspection(responseMessage))
      ) {
        throw new ProposalSentToInspectionError(
          data?.message ?? responseMessage
        );
      }

      return response.data;
    },
    onSuccess: () => invalidateAnalysisQueries(queryClient, proposalId),
    onError: async error => {
      if (isProposalSentToInspectionError(error)) {
        invalidateAnalysisQueries(queryClient, proposalId);
      }
    },
  });
}

export function useProposalAnalysisRegisterObservation(proposalId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      description: string;
      attachments: UploadedFile[];
    }) => {
      const attachmentKeys = await uploadAttachments(
        proposalId,
        payload.attachments
      );

      const response = await businessProposalsService.createAnalysisRecord(
        proposalId,
        {
          action: 'OBSERVATION',
          status: String(PortalStatus.ProposalInAnalysis),
          description: payload.description,
          ...(attachmentKeys.length > 0 && {
            fileAttachments: attachmentKeys,
          }),
        }
      );

      if (!response.success) {
        throw new Error(
          response.error || 'Erro ao registrar observação da análise'
        );
      }

      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['proposal-analysis-records', proposalId],
      });
    },
  });
}
